/**
 * Escolha do dia da tela de Pagamentos — com o calendário DIZENDO onde há trabalho.
 *
 * Substitui o `DateFilter` (um `<input type="date">` cru) nesta tela, e a diferença não é estética:
 * o financeiro abria o seletor sem nenhuma pista de onde havia dinheiro, escolhia uma data,
 * esperava a busca e às vezes achava o dia vazio. Aqui os dias com pagamento vêm marcados antes do
 * clique.
 *
 * Os três estados aparecem sempre, com a legenda embaixo (AF-4139): os botões que ligavam e
 * desligavam cada um saíram. O "Intervalo entre datas" troca a escolha de um dia pela de dois
 * cliques, início e fim, em qualquer ordem.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaCalendarAlt } from 'react-icons/fa';

import SimpleCalendar, { CalendarMarkers, dayKey } from '../../atoms/SimpleCalendar';
import ToggleSwitch from '../../atoms/ToogleSwitch';
import { PaymentCalendarDay, PaymentStatus, listPaymentsCalendar } from '../../pages/billing/services/billingApi';
import { STATUS_META } from '../../pages/billing/PaymentsPanel';

/** Os três estados, na ordem do FLUXO — a mesma do painel e da listagem. */
const HIGHLIGHTS: { status: PaymentStatus; label: string; amountOf: (d: PaymentCalendarDay) => number }[] = [
    { status: 'PENDING', label: 'Pendentes', amountOf: d => d.pendingAmount },
    { status: 'MARKED', label: 'Sinalizados', amountOf: d => d.markedAmount },
    { status: 'IDENTIFIED', label: 'Identificados', amountOf: d => d.identifiedAmount },
];

/** `YYYY-MM-DD` → `Date` local. `new Date('2026-08-19')` seria interpretado como UTC. */
export const parseDay = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
};

/** Primeiro e último dia do mês, no formato que o endpoint espera. */
export const monthRange = (year: number, month: number): [string, string] => [
    dayKey(new Date(year, month, 1)),
    dayKey(new Date(year, month + 1, 0)),
];

/**
 * Quais pontinhos cada dia recebe: um por estado que o dia tem.
 *
 * Dia SEM nada daquele estado não ganha ponto — marcar tudo seria o mesmo que não marcar nada.
 */
export const buildMarkers = (days: PaymentCalendarDay[]): CalendarMarkers => {
    const markers: CalendarMarkers = {};
    for (const day of days) {
        const dots = HIGHLIGHTS
            .filter(d => d.amountOf(day) > 0)
            .map(d => STATUS_META[d.status].hex);
        if (dots.length) markers[day.date] = dots;
    }
    return markers;
};

export interface DayRange {
    /** `YYYY-MM-DD` */
    from: string;
    /** `YYYY-MM-DD` */
    to: string;
}

interface Props {
    label: string;
    value: DayRange;
    onChange: (value: DayRange) => void;
}

const PaymentDatePicker: React.FC<Props> = ({ label, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [days, setDays] = useState<PaymentCalendarDay[]>([]);
    const [rangeMode, setRangeMode] = useState(value.from !== value.to);
    const [rangeStart, setRangeStart] = useState<string | null>(null);
    const selected = useMemo(() => parseDay(value.from), [value.from]);
    const end = useMemo(() => parseDay(value.to), [value.to]);
    const [month, setMonth] = useState<[number, number]>(
        () => [selected.getFullYear(), selected.getMonth()]);
    const box = useRef<HTMLDivElement>(null);

    const load = useCallback(async (year: number, monthIndex: number) => {
        const [from, to] = monthRange(year, monthIndex);
        try {
            // `|| []` porque a lista alimenta um `for..of`: uma resposta sem `days` derrubaria
            // a tela de Pagamentos inteira por causa do seletor de data
            setDays((await listPaymentsCalendar(from, to)) || []);
        } catch {
            // o calendário sem marcadores ainda escolhe data: falhar aqui não pode travar a tela
            setDays([]);
        }
    }, []);

    // só busca com o painel ABERTO: os marcadores não aparecem na tela fechada, e buscá-los a cada
    // troca de dia somaria uma requisição a um clique que já dispara a busca da listagem
    useEffect(() => { if (open) load(month[0], month[1]); }, [open, month, load]);

    // clicar fora fecha — sem isso o painel fica por cima da tabela até alguém escolher uma data
    useEffect(() => {
        if (!open) return;
        const onClickOutside = (event: MouseEvent) => {
            if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    const markers = useMemo(() => buildMarkers(days), [days]);

    useEffect(() => { if (!open) setRangeStart(null); }, [open]);

    const choose = (day: string) => {
        if (!rangeMode) {
            onChange({ from: day, to: day });
            setOpen(false);
        } else if (!rangeStart) {
            setRangeStart(day);
        } else {
            const [from, to] = [rangeStart, day].sort();
            onChange({ from, to });
            setOpen(false);
        }
    };

    const shown = value.from === value.to
        ? selected.toLocaleDateString('pt-BR')
        : `${selected.toLocaleDateString('pt-BR')} – ${end.toLocaleDateString('pt-BR')}`;

    return (
        <div className="relative inline-block" ref={box}>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2"
                htmlFor="seletor-data-pagamento">
                {label}
            </label>
            <button
                id="seletor-data-pagamento"
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white inline-flex items-center gap-2"
            >
                <FaCalendarAlt className="text-gray-400" />
                {shown}
            </button>

            {open && (
                <div className="absolute z-30 mt-1 left-0">
                    <SimpleCalendar
                        selectedDate={rangeStart ? parseDay(rangeStart) : selected}
                        rangeEnd={rangeStart || value.from === value.to ? null : end}
                        markers={markers}
                        onMonthChange={(year, monthIndex) => setMonth([year, monthIndex])}
                        onDateChange={date => choose(dayKey(date))}
                    />
                    <div className="bg-white dark:bg-slate-800 border border-t-0 border-gray-200 dark:border-slate-600 rounded-b-lg px-3 py-2 space-y-2">
                        <div className="flex gap-3 justify-center">
                            {HIGHLIGHTS.map(({ status, label: text }) => (
                                <span key={status} className="text-[11px] text-gray-600 dark:text-gray-300 inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full shrink-0"
                                        style={{ backgroundColor: STATUS_META[status].hex }} />
                                    {text}
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 border-t border-gray-100 dark:border-slate-700 pt-2">
                            <ToggleSwitch checked={rangeMode} ariaLabel="Intervalo entre datas"
                                onChange={on => { setRangeMode(on); setRangeStart(null); }} />
                            <span className="text-xs text-gray-700 dark:text-gray-200">Intervalo entre datas</span>
                        </div>
                        {rangeMode && (
                            <p className="text-[11px] text-gray-500">
                                {rangeStart ? 'Escolha o último dia.' : 'Escolha o primeiro dia.'}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentDatePicker;
